import type { Cart, CartLine } from "../api/carts";
import type { PurchaseOrderDetail } from "../api/purchaseOrders";
import { offerCsv } from "./offerCsv";
import { cleanDescription } from "./description";
import { formatDateShort } from "./format";

// Excel export and e-mail draft of a cart (#548). Both are built from what the
// server sent; nothing is calculated here and nothing is sent by TruuCost.

// de-DE grouping ("1.299,50") like the rest of the app; de-AT would use a
// narrow space that reads badly in a plain-text e-mail and in CSV.
const PRICE = new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const QTY = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 3 });

const HEADER = [
  "Kostenstelle",
  "Lieferadresse",
  "Großhändler",
  "Artikel",
  "Beschreibung",
  "Menge",
  "Einheit",
  "Preis",
  "Beleg",
  "Bestpreis",
  "Bestpreis bei",
  "Vorgabe",
  "Summe",
] as const;

function number(value: string | null, format: Intl.NumberFormat): string {
  return value === null || value === "" ? "" : format.format(Number(value));
}

function row(cart: Cart, line: CartLine): (string | null)[] {
  return [
    cart.cost_center?.label ?? "",
    cart.delivery_address?.label ?? "",
    line.supplier ?? "",
    line.article_code ?? line.article_group_key ?? "",
    cleanDescription(line.description) ?? "",
    number(line.qty, QTY),
    line.uom ?? "",
    number(line.reference?.price ?? line.agreed_price, PRICE),
    line.reference?.doc_number ? `${line.reference.doc_number} vom ${formatDateShort(line.reference.doc_date)}` : "",
    line.comparable_count > 1 ? number(line.best?.price ?? null, PRICE) : "",
    line.comparable_count > 1 && line.best ? line.best.supplier : "",
    number(line.target_price ?? line.agreed_price, PRICE),
    number(line.total, PRICE),
  ];
}

/** One row per position, wholesalers kept together; opens in Excel as a table. */
export function cartCsv(cart: Cart, lines: CartLine[] = cart.lines): string {
  const order = cart.groups.map((group) => group.supplier);
  const sorted = [...lines].sort(
    (a, b) => order.indexOf(a.supplier) - order.indexOf(b.supplier) || a.position - b.position,
  );
  return offerCsv([[...HEADER], ...sorted.map((line) => row(cart, line))]);
}

export function cartCsvFilename(cart: Cart, supplier?: string | null): string {
  const parts = ["Warenkorb", cart.name, supplier ?? ""].filter(Boolean).join(" ");
  return `${parts.replace(/[^\wÄÖÜäöüß .-]+/g, "_").trim()}.csv`;
}

/** Enquiry text for one wholesaler; the person sends it from their own mail program. */
export function cartEnquiry(cart: Cart, supplier: string | null, lines: CartLine[]): { subject: string; body: string } {
  const reference = cart.cost_center?.label ?? cart.name;
  const text = [
    "Guten Tag,",
    "",
    "bitte um Angebot für folgende Positionen:",
    "",
    ...lines.map((line) => {
      const description = cleanDescription(line.description);
      const quantity = `${QTY.format(Number(line.qty))}${line.uom ? ` ${line.uom}` : ""}`;
      // A special position has no article and no reference: the agreed price is
      // what was arranged, not a target we derived (#556).
      if (line.is_special) {
        const agreed = line.agreed_price;
        return `- Sonderposition${description ? `, ${description}` : ""}: ${quantity}, ${
          agreed ? `vereinbart ${PRICE.format(Number(agreed))} EUR netto` : "Preis bitte angeben"
        }`;
      }
      const target = line.target_price ?? line.agreed_price;
      const code = line.article_code ?? line.article_group_key;
      const price = target ? `Vorgabe ${PRICE.format(Number(target))} EUR netto` : "Preis bitte angeben";
      return `- ${code}${description ? `, ${description}` : ""}: ${quantity}, ${price}`;
    }),
    "",
    ...(cart.delivery_address ? [`Lieferadresse: ${cart.delivery_address.label}`, ""] : []),
    "Bitte um Rückmeldung mit Preis und Liefertermin.",
    "",
    "Freundliche Grüße",
  ].join("\n");
  return { subject: `Preisanfrage ${reference}${supplier ? ` – ${supplier}` : ""}`, body: text };
}

/** The confirmed order for the wholesaler: the reference must travel with it (#555). */
export function orderMail(order: PurchaseOrderDetail): { subject: string; body: string } {
  const text = [
    "Guten Tag,",
    "",
    `hiermit bestellen wir unter der Bestellnummer ${order.reference}:`,
    "",
    ...order.lines.map((line) => {
      const quantity = `${QTY.format(Number(line.qty))}${line.uom ? ` ${line.uom}` : ""}`;
      const price = line.unit_price ? `vereinbart ${PRICE.format(Number(line.unit_price))} EUR netto` : "Preis bitte bestätigen";
      const description = cleanDescription(line.description);
      const code = line.is_special ? "Sonderposition" : line.article_code ?? line.article_group_key;
      return `- ${code}${description ? `, ${description}` : ""}: ${quantity}, ${price}`;
    }),
    "",
    ...(order.delivery_address ? [`Lieferadresse: ${order.delivery_address}`, ""] : []),
    `Bitte geben Sie unsere Bestellnummer ${order.reference} auf der Auftragsbestätigung an.`,
    "",
    "Freundliche Grüße",
  ].join("\n");
  return { subject: `Bestellung ${order.reference}`, body: text };
}

export function orderCsv(order: PurchaseOrderDetail): string {
  const header = ["Bestellreferenz", "Bestelldatum", "Großhändler", "Kostenstelle", "Lieferadresse", "Artikel", "Beschreibung", "Menge", "Einheit", "Preis", "Vorgabe", "Summe"];
  return offerCsv([
    header,
    ...order.lines.map((line) => [
      order.reference,
      formatDateShort(order.ordered_at),
      order.supplier ?? "",
      order.cost_center.label,
      order.delivery_address ?? "",
      line.article_code ?? line.article_group_key ?? "",
      cleanDescription(line.description) ?? "",
      number(line.qty, QTY),
      line.uom ?? "",
      number(line.unit_price, PRICE),
      number(line.target_price, PRICE),
      number(line.total, PRICE),
    ]),
  ]);
}

export function mailtoHref(subject: string, body: string): string {
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
